import cx from 'clsx';
import dayjs from 'dayjs';
import { Button, Icon, Spinner } from '@vechaiui/react';
import { CheckCircleIcon, ExclamationCircleIcon } from '@heroicons/react/solid';
import { Link, Navigate, createSearchParams, useSearchParams } from 'react-router-dom';
import { useEffect } from 'react';
import { useLazyQuery } from '@apollo/client';
import { useLocalization } from '@fluent/react';

import AppContainerLayout from '#/layouts/AppContainerLayout';
import OrderCard from '#/components/cards/OrderCard';
import {
  GetOrdersByOrderNumberDocument,
  OrderCardFragment,
  OrderDetailsPanelFragment,
  OrderStatus,
  OrderCardFragmentDoc,
  PaymentMethod,
  PaymentStatusType,
} from '#/__generated__/graphql/graphql';
import { PrivateRoute } from '#/constants/page-route';
import { SearchParam } from '#/constants/search-params';
import { makeFragmentData } from '#/__generated__/graphql';

const PaymentConfirmPage = () => {
  const { l10n } = useLocalization();
  const [searchParams] = useSearchParams();
  const orderNumber = searchParams.get(SearchParam.OrderNumber);
  const [fetchOrders, { data, loading, called }] = useLazyQuery(GetOrdersByOrderNumberDocument);

  useEffect(() => {
    if (orderNumber) {
      fetchOrders({ variables: { orderNumber } });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orderNumber]);

  if (!orderNumber) {
    return <Navigate to={PrivateRoute.Home} />;
  }

  if (loading || !called) {
    return (
      <div className="flex items-center justify-center h-full">
        <Spinner size="xl" />
      </div>
    );
  }

  const order = data?.orders?.edges[0].node as OrderCardFragment & OrderDetailsPanelFragment;

  if (
    !order ||
    ![
      OrderStatus.OrderPending,
      OrderStatus.OrderProblem,
      order.payments[0].paymentMethod === PaymentMethod.CreditTerms
        ? OrderStatus.OrderPaymentDue
        : undefined,
    ].includes(order.orderStatus)
  ) {
    return <Navigate to={PrivateRoute.Home} />;
  }

  // TODO: support multiple payments
  const payment = order.payments[0];

  const isSuccessPayment = payment.paymentStatus === PaymentStatusType.PaymentCompleted;
  const isCreditTerm = payment.paymentMethod === PaymentMethod.CreditTerms;
  const creditTermValue = payment.creditPeriod?.value ?? 0;
  const creditTermUnitCode = payment.creditPeriod?.unitCode ?? 'DAY';
  const creditDate = isCreditTerm
    ? dayjs(order.orderDate).add(creditTermValue, 'day').format('DD/MM/YYYY')
    : undefined;

  const handlePaymentClick = () => {
    if (!payment.paymentUrl) {
      throw new Error('No payment url');
    }
    // Retry Payment
    window.location.href = payment.paymentUrl;
  };

  return (
    <AppContainerLayout>
      <div className="p-8 bg-white 2xl:px-12">
        <header className="flex items-center gap-2">
          <h1 className="text-3xl">
            {l10n.getString('trip-request')} {isSuccessPayment && l10n.getString('created')}
          </h1>
          <Icon
            as={isSuccessPayment ? CheckCircleIcon : ExclamationCircleIcon}
            label="check-circle"
            className={cx('w-8 h-8', isSuccessPayment ? 'text-green-500' : 'text-red-500')}
          />
        </header>
      </div>

      <div className="my-16 gap-12 flex flex-col items-center">
        <header>
          <h2
            className={cx(
              'text-3xl font-bold text-center mb-4',
              isSuccessPayment || isCreditTerm ? 'text-blue-700' : 'text-red-500',
            )}
          >
            {isCreditTerm && l10n.getString('credit-term-payment')}
            {!isCreditTerm && (
              <>
                {l10n.getString('payment')}{' '}
                {l10n.getString(isSuccessPayment ? 'successful' : 'unsuccessful')}
              </>
            )}
          </h2>

          <p className="text-xl text-center">
            {isSuccessPayment && (
              <>
                {l10n.getString('successful-message')} <br />
                {l10n.getString('successful-message-2')}
              </>
            )}
            {isCreditTerm && (
              <>
                {l10n.getString('credit-term-message')}{' '}
                <span className="font-bold">{creditDate}</span>{' '}
                <span className="font-bold text-red-500 ">
                  [{creditTermValue} {creditTermUnitCode}(s)]
                </span>
              </>
            )}
            {!isSuccessPayment && !isCreditTerm && l10n.getString('unsuccessful-message')}
          </p>
        </header>

        <div>
          <OrderCard order={makeFragmentData(order, OrderCardFragmentDoc)} />
        </div>

        <div>
          {(isSuccessPayment || isCreditTerm) && (
            <Link
              to={{
                pathname: PrivateRoute.OrderList,
                search: createSearchParams([[SearchParam.OrderNumber, orderNumber]]).toString(),
              }}
            >
              <Button className="rounded-full w-80" color="primary" size="xl" variant="solid">
                {l10n.getString('view-trip-request')}
              </Button>
            </Link>
          )}

          {!isCreditTerm && !isSuccessPayment && (
            <Button
              className="rounded-full w-80"
              color="red"
              onClick={handlePaymentClick}
              size="xl"
              variant="solid"
            >
              {l10n.getString('retry-payment')}
            </Button>
          )}
        </div>
      </div>
    </AppContainerLayout>
  );
};
export default PaymentConfirmPage;
